import { useTheme } from "@emotion/react";
import { Box, Button, CircularProgress, Typography, useMediaQuery } from "@mui/material";
import dayjs from "dayjs";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../provider/authProvider";
import LoanService from "../../services/loanService";
import BranchDropdown from "../../components/dropdowns/branchDropdown";
import CustomInput from "../../components/inputs/customInput";
import DateInput from "../../components/inputs/dateInput";
import AddIcon from '@mui/icons-material/Add';
import UserTypeDropdownV2 from "../../components/dropdowns/userTypeDropdownV2";
import PageDropdown from "../../components/dropdowns/pageDropdown";
import DriverDropdown from "../../components/dropdowns/driverDropdown";
import TransferTypeDropdown from "../../components/dropdowns/transferTypeDropdown";
import Popup from "../../components/popups/popup";


const AddLoan = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const { user } = useContext(AuthContext);
    const userPermissions = user?.permissions || [];
    const allBranchPermission = userPermissions?.some(p => p.value === 'allBranches');

    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedPage, setSelectedPage] = useState(null);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    const [selectedUserType, setSelectedUserType] = useState(null);
    const [selectedDate, setSelectedDate] = useState(dayjs());

    const [addErrors, setAddErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);

    const [formValues, setFormValues] = useState({
        amount: 0,
        type: null,
        userType: null,
        branchId: null,
        driverId: null,
        pageId: null,
        note: '',
    });

    const handleDateChange = (date) => {
        setSelectedDate(date);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    const handleNumberChange = (e) => {
        const { name, value } = e.target;
        const numericValue = value.replace(/,/g, "");

        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: numericValue,
        }));
    };

    const validateForm = () => {
        const newAddErrors = {};

        if (!formValues.amount) {
            newAddErrors.amount = t('amountRequired');
        }

        if (isNaN(formValues.amount)) {
            newAddErrors.amount = t('amountNumber');
        }

        if (!formValues.type) {
            newAddErrors.type = t('typeRequired');
        }

        if (!formValues.userType) {
            newAddErrors.userType = t('userTypeRequired');
        }

        if (formValues.userType === 'page' && !selectedPage) {
            newAddErrors.page = t('pageRequired');
        }

        if (formValues.userType === 'driver' && !selectedDriver) {
            newAddErrors.driver = t('driverRequired');
        }

        setAddErrors(newAddErrors);

        return Object.keys(newAddErrors).length === 0;
    };

    const handleUserType = (value) => {
        if (value === t("page")) return "page";
        else if (value === t("driver")) return "driver";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLoading) return;

        if (validateForm()) {
            try {
                setIsLoading(true);
                console.log();

                const data = await LoanService.add({ ...formValues, date: selectedDate });
                if (data.status === 200) {
                    setPopupMsg(data.data.message);
                    setPopupType('success');
                    setShowPopup(true);
                    setFormValues({
                        amount: 0,
                        type: null,
                        userType: null,
                        branchId: null,
                        driverId: null,
                        pageId: null,
                        note: '',
                    });
                    setSelectedBranch(null);
                    setSelectedType('');
                    setSelectedUserType('');
                    setSelectedDriver(null);
                    setSelectedPage(null);
                    setSelectedDate(dayjs());
                }

                setAddErrors({});
            } catch (error) {
                if (error.response?.status === 400) {
                    const responseErrors = error.response.data || {};
                    const formattedErrors = {};

                    Object.keys(responseErrors).forEach((field) => {
                        formattedErrors[field] = responseErrors[field];
                    });

                    setAddErrors(formattedErrors);
                } else {
                    setPopupMsg(error.response.data.error);
                    setPopupType('error');
                    console.error("An unexpected error occurred:", error);
                    setAddErrors({ general: error.response.data.error });
                }
            } finally {
                setIsLoading(false);
            }
        }
    };
    return (
        <Box sx={{
            p: isTablet ? 0.6 : 2,
            backgroundColor: theme.palette.secondary.main,
        }}>
            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: theme.palette.customGrey.borderInput,
                    px: isTablet ? 1 : 4,
                    py: 1
                }}
            >
                {
                    allBranchPermission &&
                    <BranchDropdown
                        helperText={addErrors.branch}
                        error={!!addErrors.branch}
                        setFormValues={setFormValues}
                        isFilter={false}
                        selectedBranch={selectedBranch}
                        setSelectedBranch={setSelectedBranch}
                    />
                }

                <Box sx={{ display: 'flex', flexDirection: isTablet ? 'column' : 'row', justifyContent: 'space-between' }}>
                    <Box sx={{
                        width: isTablet ? '100%' : '48%'
                    }}>
                        <UserTypeDropdownV2
                            helperText={addErrors.userType}
                            error={!!addErrors.userType}
                            setFormValues={setFormValues}
                            isFilter={false}
                            selectedUserType={selectedUserType}
                            setSelectedUserType={setSelectedUserType}
                        />
                    </Box>
                    <Box sx={{
                        width: isTablet ? '100%' : '48%'
                    }}>
                        {
                            handleUserType(selectedUserType) === 'page' ?
                                <PageDropdown
                                    helperText={addErrors.page}
                                    error={!!addErrors.page}
                                    setFormValues={setFormValues}
                                    isFilter={false}
                                    selectedPage={selectedPage}
                                    setSelectedPage={setSelectedPage}
                                /> :
                                <DriverDropdown
                                    helperText={addErrors.driver}
                                    error={!!addErrors.driver}
                                    setFormValues={setFormValues}
                                    isFilter={false}
                                    selectedDriver={selectedDriver}
                                    setSelectedDriver={setSelectedDriver}
                                />
                        }
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: isTablet ? 'column' : 'row', justifyContent: 'space-between' }}>
                    <Box sx={{ width: isTablet ? '100%' : '48%' }}>
                        <TransferTypeDropdown
                            helperText={addErrors.type}
                            error={!!addErrors.type}
                            setFormValues={setFormValues}
                            isFilter={false}
                            selectedType={selectedType}
                            setSelectedType={setSelectedType}
                            changeLangForLoan={true}
                        />
                    </Box>

                    <Box sx={{ width: isTablet ? '100%' : '48%' }}>
                        <CustomInput
                            label={t('amount')}
                            placeholder={t('amountHere')}
                            name="amount"
                            type='text'
                            value={formValues.amount ? Number(formValues.amount).toLocaleString() : 0}
                            error={!!addErrors.amount}
                            helperText={addErrors.amount}
                            onChange={handleNumberChange}
                        />
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: isTablet ? 'column' : 'row', justifyContent: 'space-between' }}>
                    <Box sx={{ width: isTablet ? '100%' : '48%' }}>
                        <DateInput
                            label={t('date')}
                            selectedDate={selectedDate}
                            setSelectedDate={setSelectedDate}
                            handleDateChange={handleDateChange}
                        />
                    </Box>

                    <Box sx={{ width: isTablet ? '100%' : '48%', height: 100 }}>
                        <CustomInput
                            label={t('note')}
                            placeholder={t('noteHere')}
                            name="note"
                            type='text'
                            value={formValues.note}
                            onChange={handleChange}
                        />
                    </Box>
                </Box>

                {addErrors.general && (
                    <Typography align="center" color="error" variant="body2" sx={{ mt: 2 }}>
                        {addErrors.general}
                    </Typography>
                )}

                <Box sx={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    mt: addErrors.general ? 1 : 4,
                    mb: 2
                }}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{ textTransform: 'none', width: 140, height: 40 }}
                    >
                        {isLoading ? <CircularProgress color="secondary" size={28} /> :
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <p>{t('add')}</p>
                                <AddIcon fontSize="small" sx={{ mx: 0.4 }} />
                            </Box>
                        }
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default AddLoan;